<script lang="ts">
	import theme from 'stores/theme';
	import CryptoStatSection from 'components/CryptoStatSection/CryptoStatSection.svelte';
	import InfoSection from 'components/InfoSection/InfoSection.svelte';
	import CoinMarketTable from 'components/CoinMarketTable/CoinMarketTable.svelte';
	import Chart from 'src/components/Chart/ChartSkeleton.svelte';
	import type { ICoinDetails } from 'customTypes/coinDetails';
	import ChartFilterTab from 'components/ChartFilterTab/ChartFilterTab.svelte';
	import type { ChartFilterType } from 'customTypes/filterTypes';
	import CoinHoldersTable from 'components/CoinHoldersTable/CoinHoldersTable.svelte';
	import CoinHoldersInfo from 'components/CoinHoldersInfo/CoinHoldersInfo.svelte';
	import NewsBlock from 'components/NewsBlock/NewsBlock.svelte';
	import GithubSection from 'components/GithubSection/GithubSection.svelte';
	import Socials from 'components/SocialNetworks/Socials.svelte';
	import SchemaOrgCoinPage from 'components/SchemaOrg/SchemaOrgCoinPage.svelte';
	import MetaTags from 'components/MetaTags/MetaTags.svelte';
	import { CANONICAL_URL } from 'utils/urls';

	export let data;
	let coinData: ICoinDetails = data.coinData;
	let totalCoinsAmount: number;

	$: dark = $theme;
	$: ({ coinData, totalCoinsAmount, route, article, marketNames, coinInfo } = data);

	let chartFilterType: ChartFilterType = 'price';
	const metaTitle = coinData?.stats?.customSEOData?.title?.length > 0
		? coinData?.stats?.customSEOData?.title
		: `${coinData.stats.name} Price Today | ${coinData.stats.symbol} to USD, Price Index & Live Chart`;
	
	const metaDescription = coinData?.stats?.customSEOData?.meta?.length > 0
		? coinData?.stats?.customSEOData?.meta
		: `Get the latest ${coinData.stats.name} price today, ${coinData.stats.symbol} market cap, price index, live chart & best exchanges to trade ${coinData.stats.symbol}. Read the latest ${coinData.stats.name} news at CryptoMarketCap.`

	const metaCanonicalURL = `${CANONICAL_URL}/coins/${data.route}`
	
</script>

<SchemaOrgCoinPage
	name={coinData.stats.name}
	route={data.route}
	symbol={coinData.stats.symbol}
	price={coinData.stats.price}
	{coinInfo}
/>
<MetaTags
	canonical={metaCanonicalURL}
	title={metaTitle}
	description={metaDescription}
	additionalMetaTags={[
		{ property: 'og:type', content: 'website' },
		{ property: 'og:title', content: metaTitle },
		{ property: 'og:description', content: metaDescription },
		{ property: 'og:image', content: `${coinData.stats.logo_url}` },
		{ property: 'og:url', content: metaCanonicalURL },
		{ property: 'og:site_name', content: 'CryptoMarketCap' },
	]}
	twitter={coinData.links.twitter_url}
/>

{#key route}
	<div class="coin-wrapper">
		<CryptoStatSection {dark} data={coinData} />
		<ChartFilterTab {dark} bind:chartFilterType />
		<Chart
			{dark}
			id={route}
			{chartFilterType}
			name={coinData.stats.name}
			coingeckoId={coinData.coingeckoId}
			coinrankingId={coinData.stats.id}
			coinmarketcapId={coinData.coinmarketcapId}
		/>

		<InfoSection
			{dark}
			stats={coinData.stats}
			copywrite={article}
			{marketNames}
			{coinInfo}
			yearPriceChange={coinData.yearPriceChangePct}
			coinsAmount={totalCoinsAmount}
		/>

		<GithubSection {dark} githubUrl={coinData.links.github_url} />

		<CoinMarketTable
			{dark}
			{route}
			symbol={coinData.stats.symbol}
			name={coinData.stats.name}
			isCustomCoin={coinData.isCustomCoin}
			coinPrice={Number(coinData.stats.price)}
		/>

		<Socials
			redditUrl={coinData.links.reddit_url}
			twitterUrl={coinData.links.twitter_url}
			id={coinData.stats.id}
			{dark}
		/>

		<CoinHoldersInfo {dark} coinName={coinData.stats.name} />
		<CoinHoldersTable {dark} symbol={coinData.stats.symbol} {route} />

		<NewsBlock abbr={coinData.stats.symbol} name={coinData.stats.name} logo={coinData.stats.logo_url} {dark} />
	</div>
{/key}

<style lang="scss">
	.coin-wrapper {
		overflow-x: hidden;
	}
</style>
